import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Header from "@/components/main/Header";
import Footer from "@/components/main/Footer";
import useProduct from "@/hooks/useProduct";
import { useTranslation } from "react-i18next";
import Contents from "@/components/main/Contents";

const Wrap = styled.div``;

const Section = styled.div`
  margin: 40px auto;
  padding: 60px 120px;
  width: calc(100% - 100px);
  background-color: #fff;
  border-radius: 20px;

  @media (max-width: 1200px) {
    width: calc(100% - 40px);
    padding: 50px 40px;
  }

  @media (max-width: 800px) {
    width: calc(100% - 40px);
    padding: 30px 20px;
  }
`;
const SectionSubTitle = styled.p`
  font-family: "Hind";
  font-size: 28px;
  color: #000;
`;
const SectionTitle = styled.p`
  margin: 10px 0 30px 0;
  font-family: "Lato Bold", sans-serif;
  font-size: 44px;
  font-weight: 600;
  color: #971b2f;
`;

const Items = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 0 0 -10px;
  width: calc(100% + 20px);
`;

const Item = styled.div`
  margin: 0 10px 20px 10px;
  width: calc(20% - 20px);
  background-color: #fff;
  border-radius: 10px;
  box-shadow: rgba(0, 0, 0, 0.04) 0 1px 0, rgba(0, 0, 0, 0.05) 0 2px 7px, rgba(0, 0, 0, 0.06) 0 12px 22px;
  overflow: hidden;

  @media (max-width: 1200px) {
    width: calc(33% - 20px);
  }

  @media (max-width: 1000px) {
    width: calc(50% - 20px);
  }

  @media (max-width: 800px) {
    width: calc(100% - 20px);
  }
`;
const ItemImg = styled.img`
  width: 100%;
`;
const ItemContent = styled.div`
  padding: 10px 24px 24px 24px;
`;
const ItemTitle = styled(SectionTitle)`
  margin: 0 0 5px 0;
  font-size: 32px;
  color: #444;
`;
const ItemDesc = styled(SectionSubTitle)`
  line-height: 26px;
  min-height: 52px;
  font-size: 16px;
  color: #676767;
  margin-bottom: 15px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;
`;

const DownloadButton = styled.button<{ disabled?: boolean }>`
  width: 100%;
  padding: 8px 16px;
  background-color: ${(props) => (props.disabled ? "#cccccc" : "#666666e6")};
  color: white;
  border: none;
  border-radius: 5px;
  font-family: "Hind", sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  transition: background-color 0.3s ease;
  position: relative;

  &:hover {
    background-color: ${(props) => (props.disabled ? "#cccccc" : "#971b2f")};
  }

  &:active {
    background-color: ${(props) => (props.disabled ? "#cccccc" : "#971b2f")};
  }
`;

// 툴팁 컨테이너
const TooltipContainer = styled.div`
  position: relative;
  flex: 1;
`;

// 툴팁 텍스트
const TooltipText = styled.div<{ show: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #f7f7f7;
  color: #666666e6;
  padding: 8px 8px 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
  margin-bottom: 5px;
  opacity: ${(props) => (props.show ? 1 : 0)};
  visibility: ${(props) => (props.show ? "visible" : "hidden")};
  transition: opacity 0.3s;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #f7f7f7;
  }
`;

export default function Page() {
  const { t } = useTranslation();
  const product = useProduct();

  // 로딩 상태와 파일 가용성을 분리
  const [fileAvailability, setFileAvailability] = useState<
    Record<
      number,
      { catalog: { exists: boolean; path: string | null }; manual: { exists: boolean; path: string | null } }
    >
  >({});
  const [isCheckingFiles, setIsCheckingFiles] = useState(false);

  // 툴팁 상태 추가
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);

  // 파일 존재 여부 확인
  const checkFileExists = async (title: string, type: string) => {
    const patterns = [
      `${title}_${type}.pdf`,
      `${title.replace(/[^a-zA-Z0-9가-힣]/g, "_")}_${type}.pdf`,
      `${title.replace(/\s+/g, "_")}_${type}.pdf`,
      `${title.toLowerCase().replace(/\s+/g, "_")}_${type}.pdf`,
      `${title
        .replace(/[가-힣]/g, "")
        .trim()
        .replace(/\s+/g, "_")}_${type}.pdf`,
    ];

    // Promise.any를 사용해서 첫 번째 성공하는 것을 반환
    try {
      const results = await Promise.allSettled(
        patterns.map(async (fileName) => {
          const filePath = `/download/${fileName}`;
          const response = await fetch(filePath, {
            method: "HEAD",
            cache: "force-cache", // 캐시 활용
          });
          if (response.ok) {
            return { exists: true, path: filePath };
          }
          throw new Error("File not found");
        })
      );

      // 첫 번째 성공한 결과 반환
      for (const result of results) {
        if (result.status === "fulfilled") {
          return result.value;
        }
      }
      return { exists: false, path: null };
    } catch (err) {
      console.log(err);
      return { exists: false, path: null };
    }
  };

  // 모든 파일을 병렬로 확인 - 핵심 개선사항
  const checkAllFilesAvailability = async (products: { id: number; title: string }[]) => {
    setIsCheckingFiles(true);

    try {
      // 모든 파일 체크를 동시에 실행
      const checkPromises = products.flatMap((product) => [
        checkFileExists(product.title, "catalog").then((result) => ({
          productId: product.id,
          type: "catalog" as const,
          result,
        })),
        checkFileExists(product.title, "manual").then((result) => ({
          productId: product.id,
          type: "manual" as const,
          result,
        })),
      ]);

      const allResults = await Promise.all(checkPromises);

      // 결과를 상태로 변환
      const availability: Record<
        number,
        { catalog: { exists: boolean; path: string | null }; manual: { exists: boolean; path: string | null } }
      > = {};

      allResults.forEach(({ productId, type, result }) => {
        if (!availability[productId]) {
          availability[productId] = {
            catalog: { exists: false, path: null },
            manual: { exists: false, path: null },
          };
        }
        availability[productId][type] = result;
      });

      setFileAvailability(availability);
    } finally {
      setIsCheckingFiles(false);
    }
  };

  useEffect(() => {
    product.fetchProductsAsync();
  }, []);

  useEffect(() => {
    if (product.state.products.length > 0) {
      checkAllFilesAvailability(product.state.products);
    }
  }, [product.state.products]);

  // 즉시 렌더링
  if (product.state.loading) {
    return <>loading...</>;
  }

  if (product.state.products.length < 1) {
    return <>Products not found.</>;
  }

  const products = [
    { title: t("menu.products.body-composition-analyzers"), products: product.state.products.slice(0, 5) },
    { title: t("menu.products.body-water-analyzers"), products: product.state.products.slice(5, 7) },
    { title: t("menu.products.blood-pressure-monitor"), products: product.state.products.slice(9, 11) },
    { title: t("menu.products.stadiometer"), products: product.state.products.slice(7, 9) },
    { title: t("menu.products.handgrip-strength-dynamometer"), products: product.state.products.slice(11, 12) },
  ];

  const handleDownload = (type: string, title: string, productId: number) => {
    const availability = fileAvailability[productId];
    if (!availability) return;

    const fileInfo = type === "catalog" ? availability.catalog : availability.manual;
    if (!fileInfo?.exists) return;

    const filePath = fileInfo.path;
    if (!filePath) return;

    const link = document.createElement("a");
    link.href = filePath;
    link.download = filePath.split("/").pop() || "download.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Header />
      <Wrap>
        {products.map((v) => (
          <Section key={v.title}>
            <SectionSubTitle>{t("menu.support.download-centre.user-manual")}</SectionSubTitle>
            <SectionTitle data-aos="fade-up" data-aos-easing="ease-out" data-aos-delay="100" data-aos-duration="500">
              {v.title}
            </SectionTitle>
            <Items>
              {v.products.map(
                (vv: { id: number; title: string; downloadCentreImage: string; brochureDesc: string }) => {
                  const availability = fileAvailability[vv.id] || {
                    catalog: { exists: false },
                    manual: { exists: false },
                  };

                  return (
                    <Item key={vv.id}>
                      <ItemImg src={vv.downloadCentreImage} />
                      <ItemContent>
                        <ItemTitle>{t(vv.title)}</ItemTitle>
                        <ItemDesc>{t(vv.brochureDesc)}</ItemDesc>
                        <ButtonContainer>
                          <TooltipContainer
                            onMouseEnter={() =>
                              (isCheckingFiles || !availability.catalog.exists) && setHoveredButton(`catalog-${vv.id}`)
                            }
                            onMouseLeave={() => setHoveredButton(null)}
                          >
                            <DownloadButton
                              disabled={isCheckingFiles || !availability.catalog.exists}
                              onClick={() => handleDownload("catalog", vv.title, vv.id)}
                            >
                              {isCheckingFiles && !availability.catalog.exists
                                ? t("menu.support.download-centre.button-loading")
                                : "Catalog"}
                            </DownloadButton>
                            <TooltipText show={hoveredButton === `catalog-${vv.id}`}>
                              {t("menu.support.download-centre.button-tooltip")}
                            </TooltipText>
                          </TooltipContainer>

                          <TooltipContainer
                            onMouseEnter={() =>
                              (isCheckingFiles || !availability.manual.exists) && setHoveredButton(`manual-${vv.id}`)
                            }
                            onMouseLeave={() => setHoveredButton(null)}
                          >
                            <DownloadButton
                              disabled={isCheckingFiles || !availability.manual.exists}
                              onClick={() => handleDownload("manual", vv.title, vv.id)}
                            >
                              {isCheckingFiles && !availability.manual.exists
                                ? t("menu.support.download-centre.button-loading")
                                : "Manual"}
                            </DownloadButton>
                            <TooltipText show={hoveredButton === `manual-${vv.id}`}>
                              {t("menu.support.download-centre.button-tooltip")}
                            </TooltipText>
                          </TooltipContainer>
                        </ButtonContainer>
                      </ItemContent>
                    </Item>
                  );
                }
              )}
            </Items>
          </Section>
        ))}
      </Wrap>
      <Contents
        title={t("menu.support.contact-us")}
        desc={t("main.contents.lets-get-in-touch.desc")}
        link={"/contact/contact-us/"}
      />
      <Footer />
    </>
  );
}
